import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Shield, FileText, MessageSquare, AlertTriangle, CreditCard, Briefcase, ShieldCheck } from 'lucide-react';

const sections = [
  { id: 'privacy', icon: Shield, title: 'Privacy Policy', content: [
    'TopFleet Logistics respects your privacy. We collect customer contact information such as name, phone number, and email for the purpose of providing logistics services, including shipment updates, scheduling, and delivery confirmations.',
    'We do not sell, rent, or share your personal information with third parties for marketing purposes.',
    'By using our services, you consent to receive SMS notifications related to your shipments. Message frequency may vary. Message and data rates may apply.',
    'You may opt out of SMS communications at any time by replying **STOP**. For assistance, reply **HELP** or contact us directly.',
    'We take reasonable measures to protect your information and ensure it is used solely for operational and service-related communication.',
  ]},
  { id: 'terms', icon: FileText, title: 'Terms & Conditions', content: [
    'By using TopFleet Logistics services, you agree to receive SMS notifications regarding your shipments, scheduling, and delivery updates.',
    'Message frequency may vary. Message and data rates may apply.',
    'For assistance, reply **HELP** or contact us directly. To opt out of SMS communications, reply **STOP** at any time.',
    'TopFleet Logistics provides transportation and logistics services and is not responsible for delays caused by factors outside of operational control, including traffic, weather, or unforeseen circumstances.',
    'Use of our services constitutes agreement to these terms.',
  ]},
  { id: 'sms', icon: MessageSquare, title: 'SMS Communications Policy', content: [
    'By providing your phone number, you agree to receive SMS messages from TopFleet Logistics related to shipment updates, scheduling, delivery confirmations, and driver coordination.',
    'Message frequency varies depending on service activity. Message and data rates may apply.',
    'You may opt out of receiving messages at any time by replying **STOP**. For assistance, reply **HELP**.',
    'We only send messages to customers who have requested services or otherwise consented to communication.',
  ]},
  { id: 'disclaimer', icon: AlertTriangle, title: 'Service Disclaimer & Liability', content: [
    'TopFleet Logistics provides transportation and logistics services with a commitment to professionalism and care.',
    'However, TopFleet Logistics is not liable for delays caused by traffic, weather conditions, mechanical issues, or other factors beyond our control.',
    'While we take reasonable care in handling shipments, liability for loss or damage may be limited in accordance with applicable laws and agreed-upon service terms.',
    'Customers are encouraged to communicate any special handling requirements in advance.',
  ]},
  { id: 'payment', icon: CreditCard, title: 'Payment & Cancellation Policy', content: [
    'Deposits may be required to secure bookings. Full or partial payment terms will be communicated prior to service.',
    'Cancellations made within 24 hours of scheduled service may be subject to a cancellation fee.',
    'Refunds, if applicable, are processed at the discretion of TopFleet Logistics and in accordance with the circumstances of the cancellation.',
  ]},
  { id: 'scope', icon: Briefcase, title: 'Service Scope', content: [
    'TopFleet Logistics provides transportation services for general freight and household goods.',
    'Services do not include hazardous materials, specialized freight, or additional services such as packing or assembly unless explicitly agreed upon in writing.',
    'All services are performed based on the agreed scope at the time of booking.',
  ]},
  { id: 'insurance', icon: ShieldCheck, title: 'Insurance & Coverage', content: [
    'TopFleet Logistics maintains applicable commercial insurance as required by law.',
    'Coverage limitations may apply depending on the nature of the shipment.',
    'Customers are encouraged to inquire about coverage details or request additional protection if needed prior to service.',
  ]},
];

const renderText = (text) => {
  const parts = text.split(/\*\*(.*?)\*\*/g);
  return parts.map((part, i) =>
    i % 2 === 1 ? <span key={i} className="text-white font-semibold">{part}</span> : part
  );
};

const LegalPolicies = () => {
  const { hash } = useLocation();

  useEffect(() => {
    if (hash) {
      const el = document.getElementById(hash.slice(1));
      if (el) {
        setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
      }
    }
  }, [hash]);

  return (
    <div className="bg-texture min-h-screen" data-testid="legal-policies-page">
      {/* Hero */}
      <section className="py-20 md:py-28 bg-brand-surface border-b border-white/10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <h1 className="font-heading text-4xl sm:text-5xl text-white mb-4">
              LEGAL & <span className="text-brand-red">POLICIES</span>
            </h1>
            <p className="text-slate-400 text-base">
              <span style={{ color: '#DC2626' }}>Top</span><span className="text-white">Fleet</span> Logistics
            </p>
          </motion.div>
        </div>
      </section>

      {/* Quick Nav */}
      <section className="py-8 border-b border-white/5">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap gap-2 justify-center">
            {sections.map((s) => (
              <a
                key={s.id}
                href={`#${s.id}`}
                className="px-3 py-1.5 rounded-lg bg-white/5 border border-white/10 text-slate-400 text-xs font-medium hover:text-white hover:border-white/20 transition-all"
                data-testid={`legal-nav-${s.id}`}
              >
                {s.title}
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Policy Sections */}
      <section className="py-16 md:py-24">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-16">
            {sections.map((section, idx) => (
              <motion.div
                key={section.id}
                id={section.id}
                className="scroll-mt-32"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-50px' }}
                transition={{ duration: 0.5, delay: idx * 0.03 }}
                data-testid={`legal-section-${section.id}`}
              >
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-xl bg-brand-red/10 flex items-center justify-center flex-shrink-0">
                    <section.icon className="w-5 h-5 text-brand-red" />
                  </div>
                  <h2 className="font-heading text-xl sm:text-2xl text-white">{section.title}</h2>
                </div>
                <div className="space-y-4 pl-[52px]">
                  {section.content.map((paragraph, pIdx) => (
                    <p key={pIdx} className="text-slate-400 text-sm leading-relaxed">
                      {renderText(paragraph)}
                    </p>
                  ))}
                </div>
                {idx < sections.length - 1 && (
                  <div className="border-b border-white/5 mt-16" />
                )}
              </motion.div>
            ))}
          </div>

          {/* Footer note */}
          <div className="mt-16 pt-8 border-t border-white/10 text-center">
            <p className="text-slate-600 text-sm">
              Last updated: {new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
            </p>
            <p className="text-slate-600 text-xs mt-1">
              TopFleet Logistics &middot; Augusta, GA &middot; <a href="tel:224-374-7400" className="text-brand-red hover:underline">224-374-7400</a>
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default LegalPolicies;
